import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { isTokenExpired } from "@maeiz/core";
import type { BroadcastConfig } from "@lib/broadcast";
import { TENANT_AUTH_LS_KEY } from "@lib/tenantPersistKeys";
import type { SessionUser } from "@models/session";

export interface AuthStore {
    user: SessionUser | null;
    token: string | null;
    isAuthenticated: boolean;
    /** True while the account is limited to paying its subscription (non-payment lock). */
    billingOnly: boolean;
    broadcast: BroadcastConfig | null;
    setAuth: (
        user: SessionUser,
        token: string,
        options?: { billingOnly?: boolean; broadcast?: BroadcastConfig | null }
    ) => void;
    setBroadcast: (broadcast: BroadcastConfig | null) => void;
    setBillingOnly: (billingOnly: boolean) => void;
    updateUser: (user: SessionUser) => void;
    clearAuth: () => void;
    checkTokenExpiry: () => boolean;
}

interface Persisted {
    user: SessionUser | null;
    token: string | null;
    isAuthenticated: boolean;
    billingOnly: boolean;
    broadcast: BroadcastConfig | null;
}

/** Writes straight to storage as well, so a full page load right after sign-in still finds the session. */
function syncPersistedState(partial: Persisted) {
    try {
        const stored = localStorage.getItem(TENANT_AUTH_LS_KEY);
        const parsed = stored ? JSON.parse(stored) : {};
        localStorage.setItem(
            TENANT_AUTH_LS_KEY,
            JSON.stringify({
                ...parsed,
                version: typeof parsed.version === "number" ? parsed.version : 0,
                state: { ...parsed.state, ...partial },
            })
        );
    } catch {
        try {
            localStorage.setItem(TENANT_AUTH_LS_KEY, JSON.stringify({ state: partial, version: 0 }));
        } catch {
            /* storage unavailable */
        }
    }
}

export const useAuthStore = create<AuthStore>()(
    persist(
        (set, get) => ({
            user: null,
            token: null,
            isAuthenticated: false,
            billingOnly: false,
            broadcast: null,
            setAuth: (user, token, options = {}) => {
                const billingOnly = options.billingOnly ?? get().billingOnly;
                const broadcast =
                    options.broadcast === undefined ? get().broadcast : options.broadcast;
                set({ user, token, isAuthenticated: true, billingOnly, broadcast });
                const sync = () =>
                    syncPersistedState({
                        user,
                        token,
                        isAuthenticated: true,
                        billingOnly,
                        broadcast,
                    });
                requestAnimationFrame(() => {
                    sync();
                    setTimeout(sync, 50);
                    setTimeout(sync, 150);
                });
            },
            setBroadcast: (broadcast) => {
                set({ broadcast });
                const { user, token, isAuthenticated, billingOnly } = get();
                if (user && token) {
                    syncPersistedState({ user, token, isAuthenticated, billingOnly, broadcast });
                }
            },
            setBillingOnly: (billingOnly) => {
                set({ billingOnly });
                const { user, token, isAuthenticated, broadcast } = get();
                if (user && token) {
                    syncPersistedState({ user, token, isAuthenticated, billingOnly, broadcast });
                }
            },
            updateUser: (user) => {
                set((state) => {
                    if (!state.token) return state;
                    syncPersistedState({
                        user,
                        token: state.token,
                        isAuthenticated: true,
                        billingOnly: state.billingOnly,
                        broadcast: state.broadcast,
                    });
                    return { user };
                });
            },
            clearAuth: () => {
                set({
                    user: null,
                    token: null,
                    isAuthenticated: false,
                    billingOnly: false,
                    broadcast: null,
                });
                try {
                    localStorage.removeItem(TENANT_AUTH_LS_KEY);
                } catch {
                    /* no-op */
                }
            },
            checkTokenExpiry: () => {
                const { token } = get();
                if (!token) return false;
                try {
                    if (isTokenExpired(token)) {
                        get().clearAuth();
                        return false;
                    }
                    return true;
                } catch {
                    return false;
                }
            },
        }),
        {
            name: TENANT_AUTH_LS_KEY,
            storage: createJSONStorage(() => localStorage),
            partialize: (s): Persisted => ({
                user: s.user,
                token: s.token,
                isAuthenticated: s.isAuthenticated,
                billingOnly: s.billingOnly,
                broadcast: s.broadcast,
            }),
        }
    )
);
